import { useEffect } from 'react';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Initialize Google Analytics
export const initializeGA = () => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', 'G-WGPRJPEVWM');
};

// Custom event tracking
export const trackEvent = (
  eventName: string,
  eventCategory: string,
  eventLabel: string,
  eventValue?: any
) => {
  if (typeof window === 'undefined') return;
  
  window.gtag('event', eventName, {
    'event_category': eventCategory,
    'event_label': eventLabel,
    'value': eventValue
  });
};

// Page view tracking
export const trackPageView = (path: string) => {
  if (typeof window === 'undefined') return;

  window.gtag('config', 'G-WGPRJPEVWM', {
    page_path: path
  });
};

// Funnel Step 1 tracking
export const trackFunnelStep1 = {
  viewAccountCreation: () => {
    trackEvent('view_account_creation', 'Step 1', 'Page View');
  },
  
  clickGoogleLogin: () => {
    trackEvent('click_google_login', 'Step 1', 'Google Login');
  },
  
  createAccount: () => {
    trackEvent('create_account', 'Step 1', 'Create Account Button');
  },
  
  clickSignIn: () => {
    trackEvent('click_sign_in', 'Step 1', 'Sign In Link');
  }
};

// Funnel Step 2 tracking
export const trackFunnelStep2 = {
  viewResumeUpload: () => {
    trackEvent('view_resume_upload', 'Step 2', 'Page View');
  },

  uploadResume: (fileName: string, fileSize: number) => {
    trackEvent('upload_resume', 'Step 2', 'Resume Uploaded', {
      file_name: fileName,
      file_size: fileSize
    });
  },

  uploadError: (error: string) => {
    trackEvent('resume_upload_error', 'Step 2', 'Upload Error', {
      error_message: error
    });
  },

  continueAfterUpload: () => {
    trackEvent('continue_after_upload', 'Step 2', 'Continue Button');
  }
};

// Funnel Step 3 tracking
export const trackFunnelStep3 = {
  viewJobDescription: () => {
    trackEvent('view_job_description', 'Step 3', 'Page View');
  },

  inputJobDescription: () => {
    trackEvent('input_job_description', 'Step 3', 'Text Input');
  },

  submitJobDescription: () => {
    trackEvent('submit_job_description', 'Step 3', 'Submit Button');
  }
};

// Funnel Step 4 tracking
export const trackFunnelStep4 = {
  viewApplicationKit: () => {
    trackEvent('view_application_kit', 'Step 4', 'Page View');
  },

  clickUnlockResume: () => {
    trackEvent('click_unlock_resume', 'Step 4', 'Unlock Resume');
  },

  clickUnlockJobMatches: () => {
    trackEvent('click_unlock_job_matches', 'Step 4', 'Unlock Job Matches');
  }
};

// Funnel step tracking
export const trackFunnelStep = (
  step: number,
  stepName: string,
  additionalParams?: Record<string, any>
) => {
  const eventName = `funnel_step_${step}`;
  trackEvent(eventName, `Step ${step}`, stepName, additionalParams);
};

// User action tracking
export const trackUserAction = (
  action: string,
  category: string,
  label?: string,
  value?: number
) => {
  trackEvent(action, category, label || '', value);
};

// Custom hook for page view tracking
export const usePageTracking = (path: string) => {
  useEffect(() => {
    trackPageView(path);
  }, [path]);
};

// Document generation tracking
export const trackDocumentGeneration = (
  documentType: 'cover_letter' | 'resume' | 'recommendations',
  success: boolean,
  errorMessage?: string
) => {
  const eventName = `generate_${documentType}`;
  trackEvent(
    eventName,
    'Document Generation',
    success ? 'Success' : 'Error',
    { success, error_message: errorMessage || null }
  );
};

// Authentication tracking
export const trackAuthentication = (
  method: 'google' | 'email' | 'anonymous',
  success: boolean,
  errorMessage?: string
) => {
  const eventName = `auth_${method}`;
  trackEvent(
    eventName,
    'Authentication',
    success ? 'Success' : 'Error',
    { success, error_message: errorMessage || null }
  );
};

// Subscription tracking
export const trackSubscription = (
  planName: string,
  planPrice: number,
  success: boolean,
  errorMessage?: string
) => {
  trackEvent(
    'subscription',
    'Subscription',
    planName,
    { 
      plan_price: planPrice,
      success,
      error_message: errorMessage || null
    }
  );
};